// DealersLanding.js
import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Badge,
  Accordion,
  Alert,
  Modal,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faGaugeHigh,
  faCloudArrowUp,
  faArrowRight,
  faCheckCircle,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { LeadsClient } from "../web-api-client.ts";

const initialFormState = {
  name: "",
  dealership: "",
  email: "",
  phone: "",
  message: "",
};

const DealersLanding = () => {
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [showDemoModal, setShowDemoModal] = useState(false);

  const openDemoModal = () => {
    setError(null);
    setSubmitted(false);
    setShowDemoModal(true);
  };

  const closeDemoModal = () => setShowDemoModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = useMemo(() => {
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email || "");
    const nameOk = !!form.name.trim();
    const dealerOk = !!form.dealership.trim();
    return { emailOk, nameOk, dealerOk };
  }, [form.email, form.name, form.dealership]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitted(false);

    // simple client-side validation
    if (!validate.nameOk) return setError("Please enter your name.");
    if (!validate.dealerOk) return setError("Please enter your dealership name.");
    if (!validate.emailOk) return setError("Please enter a valid email address.");

    setSubmitting(true);

    try {
      const client = new LeadsClient();

      // ---- AdminLeadDto (camelCase) ----
      const dto = {
        id: undefined,
        name: form.name,
        dealer: form.dealership,
        email: form.email,
        phone: form.phone,
        message: form.message,
        internalNotes: null,
        created: undefined,
        isArchived: false,
      };

      const ok = await client.postApiLeadsAdmin(dto);

      if (ok) {
        setSubmitted(true);
        setForm(initialFormState);
        // keep modal open so they see confirmation
      } else {
        setError("Something went wrong sending your message. Please try again.");
      }
    } catch (err) {
      console.error("Dealer lead submission error:", err);
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  // Render function (not a nested component) to avoid focus-loss issues
  const renderContactForm = (compact = false) => (
    <>
      <Form onSubmit={handleSubmit}>
        <Row className={compact ? "g-2" : "g-3"}>
          {[
            { id: "name", label: "Your Name", placeholder: "Jane Doe" },
            { id: "dealership", label: "Dealership", placeholder: "Acme Auto Group" },
            { id: "email", label: "Email", placeholder: "you@dealership.com", type: "email" },
            { id: "phone", label: "Phone", placeholder: "(555) 123-4567", type: "tel", required: false },
          ].map((f) => (
            <Col md={6} key={f.id}>
              <Form.Group controlId={`contact-${compact ? "modal-" : ""}${f.id}`}>
                <Form.Label>{f.label}</Form.Label>
                <Form.Control
                  type={f.type || "text"}
                  name={f.id}
                  value={form[f.id]}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                  className="bg-secondary text-light border-0"
                  required={f.required !== false && f.id !== "phone"}
                  disabled={submitting}
                />
              </Form.Group>
            </Col>
          ))}

          <Col xs={12}>
            <Form.Group controlId={`contact-${compact ? "modal-" : ""}message`}>
              <Form.Label>What are you hoping to improve?</Form.Label>
              <Form.Control
                as="textarea"
                rows={compact ? 3 : 4}
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your inspection or sales workflow."
                className="bg-secondary text-light border-0"
                disabled={submitting}
              />
            </Form.Group>
          </Col>

          <Col xs={12} className="d-flex justify-content-end gap-2">
            {compact && (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={closeDemoModal}
                disabled={submitting}
              >
                Close
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Sending..." : "Send Message"}
            </Button>
          </Col>
        </Row>
      </Form>

      {/* SUCCESS + ERROR */}
      {submitted && (
        <Alert variant="success" className="mt-3 py-2 small mb-0">
          Thanks! We’ve received your message.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mt-3 py-2 small mb-0">
          {error}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <style>{`
        .text-primary { color: #82bb67 !important; }

        /* Modal: dark + mobile-friendly (similar to DVI lead modal) */
        .demo-modal-dialog {
          width: auto !important;
          max-width: 720px !important;
          margin: 0.75rem auto;
        }
        .demo-modal-content {
          border-radius: 14px;
          overflow: hidden;
          background: #151a20 !important;
          color: rgba(255,255,255,0.92) !important;
          border: 1px solid rgba(255,255,255,0.10) !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.55);
        }
        .demo-modal-header {
          background: #151a20 !important;
          color: rgba(255,255,255,0.92) !important;
          border-bottom: 1px solid rgba(255,255,255,0.10) !important;
        }
        .demo-modal-body {
          background: #151a20 !important;
          color: rgba(255,255,255,0.92) !important;
        }
        .demo-modal-content .form-label,
        .demo-modal-content label,
        .demo-modal-content .form-check-label {
          color: rgba(255,255,255,0.82) !important;
        }
        .demo-modal-content .text-muted,
        .demo-modal-content .small,
        .demo-modal-content small {
          color: rgba(255,255,255,0.60) !important;
        }
        .demo-modal-close {
          color: rgba(255,255,255,0.70) !important;
          text-decoration: none;
          font-size: 1.25rem;
          line-height: 1;
        }
        .demo-modal-close:hover { color: rgba(255,255,255,0.95) !important; }

        @media (max-width: 575.98px) {
          .demo-modal-dialog {
            width: auto !important;
            max-width: calc(100vw - 28px) !important;
            margin: 14px !important;
            box-sizing: border-box;
          }
          .demo-modal-content {
            width: 100% !important;
            max-width: 100% !important;
            max-height: 84vh;
            border-radius: 14px;
            box-sizing: border-box;
          }
          .demo-modal-body {
            max-height: calc(84vh - 64px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 18px;
          }
          .modal { padding-right: 0 !important; }
          .modal-backdrop.show { opacity: 0.45; }
          .modal {
            backdrop-filter: blur(2px);
            -webkit-backdrop-filter: blur(2px);
          }
        }

        .demo-modal-content * { max-width: 100%; }
      `}</style>

      {/* HERO */}
      <section className="py-5 bg-dark text-light border-bottom border-secondary">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={12}>
              <img
                src="logo-light.png"
                alt="Logo"
                className="logo print-hidden"
                style={{ maxWidth: "250px", marginRight: "10px" }}
              />
              <h1 className="display-5 fw-semibold">
                Turn every customer visit into{" "}
                <span className="text-primary">trust & revenue</span>.
              </h1>
              <p className="lead text-muted mt-3">
                Q-caR helps dealers create transparent Digital Vehicle
                Inspections with photos, notes, and shareable links—so customers
                know what they are buying and your team knows they are interested.
              </p>

              <div className="d-flex gap-3 mt-4">
                {/* Changed: opens modal instead of anchor */}
                <Button size="lg" variant="primary" onClick={openDemoModal}>
                  Request a Demo{" "}
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Button>
              </div>

              <div className="d-flex align-items-center gap-3 mt-4">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                <small className="text-muted">
                  No long-term contract • Quick setup • Works on desktop & mobile
                </small>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-5 bg-dark text-light border-top border-secondary">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-semibold">Why dealers choose Q-caR</h2>
              <p className="text-muted mb-0">
                Speed up approvals, reduce back-and-forth, and boost customer trust.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              {
                icon: faClipboardCheck,
                title: "Tap-friendly DVI workflow",
                desc: "Prebuilt checklists and simple pass/fail statuses help techs finish inspections fast.",
              },
              {
                icon: faCloudArrowUp,
                title: "Photo-rich transparency",
                desc: "Capture and share clean, mobile-friendly inspection reports instantly.",
              },
              {
                icon: faGaugeHigh,
                title: "Qualified Leads",
                desc: "Know what customers want and capture walk-in interest effortlessly.",
              },
            ].map((f, idx) => (
              <Col md={4} key={idx}>
                <Card className="bg-secondary text-light border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="mb-3">
                      <FontAwesomeIcon icon={f.icon} className="text-primary me-2" />
                      <span className="fw-semibold">{f.title}</span>
                    </div>
                    <p className="text-muted mb-0">{f.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-5 bg-body-tertiary text-light border-top border-secondary">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-semibold">How it works</h2>
              <p className="text-muted mb-0">
                Simple setup. Start sending beautiful, actionable inspections today.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {[
              { step: "1", title: "Create an inspection", desc: "Scan VIN or choose a vehicle." },
              { step: "2", title: "Add photos & notes", desc: "Show customers issues clearly." },
              { step: "3", title: "Share & capture leads", desc: "Let walk-ins discover your cars." },
            ].map((s, idx) => (
              <Col md={4} key={idx}>
                <Card className="bg-dark border-secondary h-100">
                  <Card.Body>
                    <div className="display-6 fw-bold text-primary mb-2">{s.step}</div>
                    <h5 className="fw-semibold">{s.title}</h5>
                    <p className="text-muted mb-0">{s.desc}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-5 bg-dark text-light border-top border-secondary">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-semibold">FAQs</h2>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Accordion flush>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>How quickly can we start?</Accordion.Header>
                  <Accordion.Body className="bg-dark text-muted">
                    Most dealerships start the same day.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>Does this work on mobile?</Accordion.Header>
                  <Accordion.Body className="bg-dark text-muted">
                    Yes — Q-caR is fully mobile-friendly.
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>Can I customize the checklist?</Accordion.Header>
                  <Accordion.Body className="bg-dark text-muted">
                    Absolutely — tailor items to match your workflow.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CONTACT SECTION (form still present at bottom) */}
      <section id="contact" className="py-5 bg-body-tertiary text-light">
        <Container>
          <Row className="mb-4">
            <Col md={8}>
              <h2 className="fw-semibold">Get in touch</h2>
              <p className="text-muted mb-0">Tell us about your dealership — we'll reach out soon.</p>
            </Col>
          </Row>

          <Row className="g-4">
            {/* FORM (still present) */}
            <Col md={7}>
              <Card className="bg-dark border-secondary shadow-sm">
                <Card.Body>{renderContactForm(false)}</Card.Body>
              </Card>

              
            </Col>

            {/* SIDEBAR */}
            <Col md={5}>
              <Card className="bg-dark border-secondary h-100 shadow-sm">
                <Card.Body>
                  <h5 className="fw-semibold mb-3">What you’ll get</h5>
                  <ul className="text-muted mb-0">
                    <li className="mb-2">A short discovery call</li>
                    <li className="mb-2">A guided tour of Q-caR</li>
                    <li className="mb-2">Pricing & next steps</li>
                  </ul>
                  <div className="mt-4">
                    <Badge bg="success" pill>Quick setup</Badge>{" "}
                    <Badge bg="info" pill>Mobile-ready</Badge>{" "}
                    <Badge bg="secondary" pill>No long-term contract</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* DEMO MODAL */}
      <Modal
        show={showDemoModal}
        onHide={closeDemoModal}
        centered
        dialogClassName="demo-modal-dialog"
        contentClassName="demo-modal-content"
        backdrop
        keyboard
      >
        <Modal.Header className="demo-modal-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="pe-2">
              <div className="fw-semibold">Request a demo</div>
              <div className="text-muted small">Tell us about your dealership — we’ll reach out soon.</div>
            </div>
            <Button
              variant="link"
              className="p-0 demo-modal-close"
              onClick={closeDemoModal}
              aria-label="Close"
              title="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="demo-modal-body">
          {renderContactForm(true)}
        </Modal.Body>
      </Modal>

      {/* FOOTER */}
      <footer className="py-4 bg-dark border-top border-secondary text-center text-muted small">
        © {new Date().getFullYear()} Q-caR • All rights reserved.
      </footer>
    </>
  );
};

export default DealersLanding;
